import React, { useMemo } from "react";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";
import { FilterAltOutlined as FilterAltOutlinedIcon } from "../icons/FigmaControlIcons";

import InfoActionIcons from "../InfoActionIcons";
import {
  buildCompanyProfileActions,
  getPrimaryCompanyWebsite,
} from "../../utils/companySocialLinks";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  toolbarPillButtonSx,
  withPillButtonClassName,
} from "../../utils/pillButtonStyles";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const CONTACT_ACTION_TYPES = new Set(["website", "email", "phone"]);
const SOCIAL_ACTION_TYPES = new Set([
  "linkedin",
  "facebook",
  "twitter",
  "x",
  "google",
  "instagram",
  "youtube",
  "tiktok",
]);

export const TAB_BLUE = "#3451F4";
export const TAB_PURPLE = "#9C4FFF";
export const TAB_LIGHT_BLUE = "#7BC4FF";
export const TAB_BORDER = "#D7E0EA";

const resolveAssetUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/uploads/") || raw.startsWith("uploads/")) {
    const normalized = raw.startsWith("/") ? raw : `/${raw}`;
    return `${API_BASE}${normalized}`;
  }
  return raw;
};

const formatWebsiteLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return new URL(normalized).hostname.replace(/^www\./i, "");
  } catch {
    return raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "");
  }
};

export function CompanyTabSummaryRow({ company = {}, onFilterClick }) {
  const companyName = String(company?.name || company?.company_name || "Company").trim();
  const companyLogo = resolveAssetUrl(company?.logo || company?.company_logo || "");
  const companyWebsite = formatWebsiteLabel(getPrimaryCompanyWebsite(company) || "");

  const companyActions = useMemo(() => {
    const fallbackActions = buildCompanyProfileActions(company);

    return [
      { type: "website", href: getPrimaryCompanyWebsite(company) || "" },
      {
        type: "email",
        href: company?.contact_email || company?.company_email || company?.email || "",
      },
      {
        type: "phone",
        href: company?.contact_phone || company?.company_phone || company?.phone || "",
      },
      ...fallbackActions.filter((action) => SOCIAL_ACTION_TYPES.has(action?.type)),
    ];
  }, [company]);
  const contactActions = useMemo(
    () => companyActions.filter((action) => CONTACT_ACTION_TYPES.has(action?.type)),
    [companyActions]
  );
  const socialActions = useMemo(
    () => companyActions.filter((action) => SOCIAL_ACTION_TYPES.has(action?.type)),
    [companyActions]
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 2.25,
          pb: 2.75,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.9,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <Avatar
            src={companyLogo || undefined}
            sx={{
              width: 40,
              height: 40,
              bgcolor: companyLogo ? "#F3E8FF" : "#2F6FFF",
              background: companyLogo
                ? "#F3E8FF"
                : "linear-gradient(135deg, #5EA8FF, #3451F4)",
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {companyName[0] || "C"}
          </Avatar>

          <Box sx={{ minWidth: 0, mr: { xs: 0, md: 1 } }}>
            <Typography
              sx={{ fontSize: 15.5, fontWeight: 500, color: "#344054", lineHeight: 1.15 }}
            >
              {companyName}
            </Typography>
            <Typography
              sx={{ mt: 0.3, fontSize: 14, color: "#9C4FFF", lineHeight: "24px" }}
            >
              {companyWebsite || "-"}
            </Typography>
          </Box>

          <Box
            aria-hidden="true"
            sx={{
              width: "1px",
              height: 26,
              backgroundColor: "#CBD5E1",
              mx: { xs: 0.4, md: 0.8 },
              flex: "0 0 auto",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.1,
              flexWrap: "wrap",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <InfoActionIcons
              actions={contactActions}
              gap={1.1}
              showDisabled={true}
              showGroupDivider={false}
              preserveOrder={true}
            />

            {contactActions.length && socialActions.length ? (
              <Box
                aria-hidden="true"
                sx={{
                  width: "1px",
                  height: 20,
                  backgroundColor: "#CBD5E1",
                  flex: "0 0 auto",
                }}
              />
            ) : null}

            <InfoActionIcons
              actions={socialActions}
              gap={1.1}
              showDisabled={true}
              showGroupDivider={false}
              preserveOrder={true}
            />
          </Box>
        </Box>

        <Button
          variant="contained"
          className={withPillButtonClassName()}
          onClick={onFilterClick}
          startIcon={<FilterAltOutlinedIcon sx={{ fontSize: 18 }} />}
          sx={[
            pillButtonInteractiveSx,
            pillButtonFilledSx,
            toolbarPillButtonSx,
            {
              alignSelf: { xs: "flex-end", md: "center" },
            },
          ]}
        >
          Filter By
        </Button>
      </Box>
    </>
  );
}

export function CompanyTabSectionHeader({ title, actions = [] }) {
  return (
    <Box
      sx={{
        py: 2.25,
        mb: 3,
        borderTop: `1px solid ${TAB_LIGHT_BLUE}`,
        borderBottom: `1px solid ${TAB_LIGHT_BLUE}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography
          sx={{
            color: TAB_BLUE,
            fontSize: 20,
            fontWeight: 700,
            fontFamily: "Be Vietnam Pro",
          }}
        >
          {title}
        </Typography>

        {actions.length ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {actions.map((action, index) => (
              <React.Fragment key={`${action?.label || "action"}-${index}`}>
                {index > 0 ? (
                  <Typography sx={{ fontSize: 13, color: "#D0D5DD" }}>|</Typography>
                ) : null}
                <Typography
                  component="button"
                  type="button"
                  onClick={action?.onClick}
                  sx={{
                    border: 0,
                    background: "transparent",
                    p: 0,
                    m: 0,
                    fontSize: 13,
                    color: "#7C3AED",
                    fontWeight: 600,
                    cursor: action?.onClick ? "pointer" : "default",
                    "&:hover": action?.onClick ? { textDecoration: "underline" } : undefined,
                  }}
                >
                  {action?.label || ""}
                </Typography>
              </React.Fragment>
            ))}
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}
